import React, { useEffect, useMemo, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import HighlightConfig from "./components/section/HighlightConfig";
import {
  createComponent,
  updateComponent,
} from "../../api/legacy/ComponentAPI";
import ProjectAPI from "../../api/ProjectAPI";
import history from "../../components/common/navigation/AppHistory";
import Breadcrumbs from "../../components/utils/Breadcrumbs";
import ContainerHeader from "../../components/utils/ContainerHeader";
import { COMPONENT_TYPES } from "../../constants/componentType";
import ItemStatus from "../../utils/ItemStatus";

import type { ChangeEvent, JSX } from "react";

import type {
  GetProjectPageComponentListResponse,
  GetProjectPageResponse,
  HighlightComponent,
  HighlightParams,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";
import type { HighlightContentType } from "../../constants/highlight";
import type { AppState } from "../../reducers";

const containerHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097766-the-smart-components-editor-of-your-homepage",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097766-l-editeur-des-smart-composants-de-votre-page-d-accueil",
};
const { HIGHLIGHT } = COMPONENT_TYPES;

function PlatformComponentHighlight(): JSX.Element {
  const dispatch = useDispatch();
  const { projectSlug, platform, componentId, pageId } =
    useParams<PlatformRoutesParams>();
  const { t } = useTranslation("editor-highlight");

  const token = useSelector((state: AppState) => state.loginReducer.token);

  const isNew = componentId === "new";

  const queryClient = useQueryClient();

  const getComponentQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getComponent<HighlightComponent>,
    params: [{ projectSlug, componentId }],
    options: {
      enabled: !isNew,
    },
  });

  const getProjectPageComponentListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageComponentList,
    params: [{ pageId, platform, projectSlug }],
    options: { enabled: isNew },
  });

  const { data: highlightData } = useQuery(getComponentQuery);

  const [highlightComponent, setHighlightComponent] =
    useState<HighlightComponent>();

  const { data: projectPageComponentListData } = useQuery({
    ...getProjectPageComponentListQuery,
    select: (data) => data.components,
  });

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const newHighlightData = useMemo(
    () => ({
      section: "page" as const,
      storefront_project_page_uuid: pageId,
      type: HIGHLIGHT,
      platform,
      label: t("newHighlight"),
      enabled: true,
      params: {
        contentType: "hosted" as const,
        mediaId: null,
        trailerId: null,
        isTitleDisplayed: true,
        isDescriptionDisplayed: true,
        isButtonDisplayed: true,
        isClickableComponent: false,
        isLoopVideo: false,
      },
      rank:
        projectPageComponentListData?.length !== undefined
          ? projectPageComponentListData.length + 1
          : 0,
      last_rank: null,
      dynamic_component: null,
      static_component: null,
      uuid: "new",
    }),
    [pageId, platform, t, projectPageComponentListData?.length],
  );

  useEffect(() => {
    if (isNew) {
      setHighlightComponent(newHighlightData);
    } else if (highlightData) {
      setHighlightComponent(highlightData);
    }
  }, [isNew, highlightData, newHighlightData]);

  const breadcrumbs = [
    {
      name: t("editor-menu:platform"),
      url: "/layout",
    },
    {
      name: t("editor-platform:title.layout"),
      url: "/layout",
    },
    {
      name: t("editor-platform:page"),
      url: `/layout/pages/${pageId}/${platform}`,
    },
    {
      name: t("highlight"),
    },
  ];

  const handleChangeComponent = (
    prop: keyof HighlightComponent,
    value: string | boolean,
  ): void => {
    if (!highlightComponent) return;

    setHighlightComponent({
      ...highlightComponent,
      [prop]: value,
    });
  };

  const handleChangeContentType = (
    contentTypeSelected: HighlightContentType,
  ): void => {
    switch (contentTypeSelected) {
      case "hosted":
        setHighlightComponent((prevComponent) => {
          if (!prevComponent) return prevComponent;
          return {
            ...prevComponent,
            params: {
              contentType: "hosted",
              mediaId: null,
              trailerId: null,
              isTitleDisplayed: prevComponent.params.isTitleDisplayed,
              isDescriptionDisplayed:
                prevComponent.params.isDescriptionDisplayed,
              isButtonDisplayed:
                "isButtonDisplayed" in prevComponent.params
                  ? prevComponent.params.isButtonDisplayed
                  : true,
              isClickableComponent:
                "isClickableComponent" in prevComponent.params
                  ? prevComponent.params.isClickableComponent
                  : true,
              has_conditions: prevComponent.params.has_conditions,
              conditional_render: prevComponent.params.conditional_render,
              isLoopVideo:
                "isLoopVideo" in prevComponent.params
                  ? prevComponent.params.isLoopVideo
                  : false,
            },
          };
        });
        break;
      case "offer":
        setHighlightComponent((prevComponent) => {
          if (!prevComponent) return prevComponent;
          return {
            ...prevComponent,
            params: {
              contentType: "offer",
              offerPlanId: null,
              isTitleDisplayed: prevComponent.params.isTitleDisplayed,
              isDescriptionDisplayed:
                prevComponent.params.isDescriptionDisplayed,
              has_conditions: prevComponent.params.has_conditions,
              conditional_render: prevComponent.params.conditional_render,
              ratio:
                "ratio" in prevComponent.params
                  ? prevComponent.params.ratio
                  : "169",
            },
          };
        });
        break;
      case "fast":
      case "external":
        setHighlightComponent((prevComponent) => {
          if (!prevComponent) return prevComponent;
          return {
            ...prevComponent,
            params: {
              contentType: contentTypeSelected,
              mediaId: null,
              isEpgDisplayed: false,
              isTitleDisplayed: prevComponent.params.isTitleDisplayed,
              isDescriptionDisplayed:
                prevComponent.params.isDescriptionDisplayed,
              isButtonDisplayed:
                "isButtonDisplayed" in prevComponent.params
                  ? prevComponent.params.isButtonDisplayed
                  : true,
              isClickableComponent:
                "isClickableComponent" in prevComponent.params
                  ? prevComponent.params.isClickableComponent
                  : true,
              has_conditions: prevComponent.params.has_conditions,
              conditional_render: prevComponent.params.conditional_render,
            },
          };
        });
        break;
      case "live":
        setHighlightComponent((prevComponent) => {
          if (!prevComponent) return prevComponent;
          return {
            ...prevComponent,
            params: {
              contentType: "live",
              mediaId: null,
              isTitleDisplayed: prevComponent.params.isTitleDisplayed,
              isDescriptionDisplayed:
                prevComponent.params.isDescriptionDisplayed,
              isButtonDisplayed:
                "isButtonDisplayed" in prevComponent.params
                  ? prevComponent.params.isButtonDisplayed
                  : true,
              isClickableComponent:
                "isClickableComponent" in prevComponent.params
                  ? prevComponent.params.isClickableComponent
                  : true,
              has_conditions: prevComponent.params.has_conditions,
              conditional_render: prevComponent.params.conditional_render,
            },
          };
        });
        break;
      default:
        console.error("Unknown content type selected:", contentTypeSelected);
        break;
    }
  };

  const handleChangeParamsComponent = (
    updates: Partial<HighlightParams>,
  ): void => {
    setHighlightComponent((prevComponent) => {
      if (!prevComponent) return prevComponent;
      return {
        ...prevComponent,
        params: {
          ...prevComponent.params,
          ...updates,
        },
      } as HighlightComponent;
    });
  };

  const handleSaveComponent = (): void => {
    if (!highlightComponent) return;

    const componentWithParams = {
      ...highlightComponent,
      params: {
        ...highlightComponent.params,
        conditional_render:
          highlightComponent.params?.conditional_render?.filter(
            (condition) =>
              !!(condition.type && condition.operator && condition.predicat),
          ),
      },
    };

    if (isNew) {
      dispatch(
        createComponent(
          componentWithParams,
          token,
          projectSlug,
          t,
          (response: HighlightComponent) => {
            const oldPath = history.location.pathname;
            const newPath = oldPath.replace(/\/(new)$/, `/${response.uuid}`);
            history.replace(newPath);
            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.concat(response),
                };
              },
            );

            const hasPlatformInPage =
              projectPageData?.components_platform?.includes(platform);

            if (!hasPlatformInPage) {
              queryClient.setQueryData<GetProjectPageResponse>(
                getProjectPageQuery.queryKey,
                (oldData) => {
                  if (!oldData) return oldData;
                  return {
                    ...oldData,
                    components_platform: [
                      ...(oldData?.components_platform ?? []),
                      platform,
                    ],
                  };
                },
              );
            }
          },
        ),
      );
    } else {
      dispatch(
        updateComponent(
          componentWithParams,
          token,
          projectSlug,
          t,
          (response: HighlightComponent) => {
            queryClient.setQueryData(
              getProjectPageComponentListQuery.queryKey,
              (
                oldData?: GetProjectPageComponentListResponse,
              ): GetProjectPageComponentListResponse | undefined => {
                if (!oldData) return undefined;
                return {
                  ...oldData,
                  components: oldData.components.map((item) =>
                    item.uuid === response.uuid ? response : item,
                  ),
                };
              },
            );
            queryClient.setQueryData(getComponentQuery.queryKey, response);
          },
        ),
      );
    }
  };

  return (
    <div className="editor-container editor-platform editor-has-breadcrumbs">
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        infoLinks={containerHeaderLinks}
        item={highlightComponent ?? {}}
        itemStatus={ItemStatus.getStatus(highlightComponent)}
        onSave={handleSaveComponent}
        onChangeLabel={(
          target: ChangeEvent<HTMLInputElement>["target"],
        ): void => handleChangeComponent("label", target.value)}
        onChangeState={(isEnable: boolean): void =>
          handleChangeComponent("enabled", isEnable)
        }
      />

      <div className="editor-tile-container">
        {highlightComponent ? (
          <HighlightConfig
            componentParams={highlightComponent.params}
            onChange={handleChangeParamsComponent}
            onChangeContentType={handleChangeContentType}
          />
        ) : null}
      </div>
    </div>
  );
}

export default PlatformComponentHighlight;
